<script setup lang="ts">
import SubscriptionPlanAddEditDialog from '@/components/dialogs/SubscriptionPlanAddEditDialog.vue'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'

definePage({
  meta: {
    action: 'manage',
    subject: 'all',
  },
})

const { t } = useI18n()
const table = 'subscription-plans'

const headers = [
  { title: t(`${table}-page.name`), key: 'name' },
  { title: t(`${table}-page.description`), key: 'description', sortable: false },
  { title: t(`${table}-page.period`), key: 'periodicityType', sortable: false },
  { title: t(`${table}-page.grace_days`), key: 'graceDays', sortable: false },
  { title: t(`${table}-page.features`), key: 'features', sortable: false },
  { title: t(`${table}-page.actions`), key: 'actions', sortable: false },
]

const searchQuery = ref('')
const debounced = useDebounce(searchQuery, 300)

const tableOptions = useCookie<any>(`${table}:options`, {
  default: () => ({
    page: 1,
    itemsPerPage: 10,
    sortBy: 'id',
    orderBy: 'desc',
  }),
})

const query = computed(() => ({
  search: debounced.value,
  page: tableOptions.value.page,
  itemsPerPage: tableOptions.value.itemsPerPage,
  sortBy: tableOptions.value.sortBy,
  orderBy: tableOptions.value.orderBy,
}))

const url = createUrl(`/${table.replace('-', '/')}/`, { query })
const { data: itemsData, execute: fetchItems } = await useApi<any>(url)

const items = computed(() => itemsData.value?.data ?? [])
const total = computed(() => itemsData.value?.total ?? 0)
const isLoading = ref(false)

const isAddDialogOpen = ref(false)
const currentItem = ref<any>(undefined)

const isDeleteDialogOpen = ref(false)
const deleteItemId = ref<number | null>(null)

const openEditDialog = (item: any) => {
  currentItem.value = item
  isAddDialogOpen.value = true
}

const openDeleteDialog = (id: number) => {
  deleteItemId.value = id
  isDeleteDialogOpen.value = true
}

const handleDelete = async (confirmed: boolean) => {
  if (confirmed && deleteItemId.value) {
    await $api(`/subscription/plans/${deleteItemId.value}`, { method: 'DELETE' })
    await fetchItems()
  }
}

const updateList = async () => {
  isLoading.value = true
  await fetchItems()
  isLoading.value = false
}

const periodLabel = (type: string | null) => {
  if (!type) {
    return t(`${table}-page.permanent`)
  }

  const labels: Record<string, string> = {
    Month: t(`${table}-page.monthly`),
    Year: t(`${table}-page.yearly`),
  }

  return labels[type] ?? type
}

watch(isAddDialogOpen, val => {
  if (!val) {
    setTimeout(() => {
      currentItem.value = undefined
    }, 200)
  }
})
</script>

<template>
  <VCard>
    <VCardText class="d-flex flex-wrap gap-4">
      <VTextField
        v-model="searchQuery"
        :placeholder="t('search')"
        density="compact"
        style="max-inline-size: 250px;"
      />
      <VSpacer />
      <VBtn
        color="primary"
        @click="isAddDialogOpen = true"
      >
        {{ t(`${table}-page.add`) }}
      </VBtn>
    </VCardText>

    <VDataTableServer
      v-model:items-per-page="tableOptions.itemsPerPage"
      v-model:page="tableOptions.page"
      :items="items"
      :items-length="total"
      :headers="headers"
      :loading="isLoading"
      item-value="id"
    >
      <template #item.features="{ item }">
        <div>
          <VChip
            v-for="feature in item.features"
            :key="feature.feature"
            size="small"
            class="me-1 mb-1"
          >
            {{ t(`${table}-page.${feature.feature}`) }}: {{ feature.charges }}
          </VChip>
        </div>
      </template>

      <template #item.periodicityType="{ item }">
        {{ periodLabel(item.periodicityType) }}
      </template>

      <template #item.actions="{ item }">
        <IconBtn @click="openEditDialog(item)">
          <VIcon icon="tabler-edit" />
        </IconBtn>
        <IconBtn @click="openDeleteDialog(item.id)">
          <VIcon icon="tabler-trash" />
        </IconBtn>
      </template>

      <template #bottom>
        <VDivider />
        <div class="d-flex align-center justify-end flex-wrap gap-3 pa-5 pt-3">
          <AppSelect
            v-model="tableOptions.itemsPerPage"
            :items="[10, 25, 50, 100]"
            style="max-inline-size: 6.25rem;"
          />
          <VPagination
            v-model="tableOptions.page"
            :total-visible="5"
            :length="Math.ceil(total / tableOptions.itemsPerPage)"
          />
        </div>
      </template>
    </VDataTableServer>
  </VCard>

  <SubscriptionPlanAddEditDialog
    v-model:isDialogVisible="isAddDialogOpen"
    :plan-data="currentItem"
    @submit="updateList"
  />

  <ConfirmDialog
    v-model:isDialogVisible="isDeleteDialogOpen"
    :confirmation-question="t(`${table}-page.delete_confirm`)"
    :confirm-title="t(`${table}-page.delete`)"
    confirm-msg=""
    :cancel-title="t('cancel')"
    cancel-msg=""
    @confirm="handleDelete"
  />
</template>
